import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import bem from 'utils/bem';
import { observer } from 'mobx-react-lite';
import Text from 'components/Text';
import Model from '../../../../model';
import CityField from 'forms/fields/CityField';
import DistrictField from 'forms/fields/DistrictField';
import PairsCheckboxField from 'forms/fields/PairsCheckboxField';
import RadioField from 'forms/fields/RadioField';
import SpecialistSearchField from 'forms/fields/SpecialistSearchField';
import EventsField from 'forms/fields/EventsField';
import {
    FormSpecialistTeaser
} from 'components/SpecialistTeaser';
import { useFormikContext } from 'formik';
import Icon from 'components/Icon';
import Slider from 'components/Slider';
import { useMobile } from 'utils/media';
import MobileSlider from 'components/Slider/MobileSlider';
import './index.sass';
import { times } from 'utils/render';
import { TeaserSkeleton } from 'components/SpecialistTeaser/FormSpecialistTeaser';
import OfflineRadioField from 'forms/fields/OfflineRadioField';
import PaymentBalanceField from 'forms/fields/PaymentBalanceField';
import { proceedToCheckout } from '../../../../model/utils';
import KZNotEnoughBalance from 'components/KZNotEnoughBalance';
import ProblemsAutocompleteField from 'forms/fields/ProblemsAutocompleteField';
import UserInfo from 'forms/forms/OrderForm/UserInfo';
import OrderInfo from 'forms/forms/OrderForm/OrderInfo';
import Tickets from 'forms/forms/OrderForm/Tickets';
import Summary from 'forms/forms/OrderForm/Summary';
import { ForeignCardWidget } from 'components/ForeignCardWidget';

const { block, el } = bem('order-form', 'form');

const FirstStep = observer(() => {
    const { specialist } = Model.orderForm;

    const { problemsOptions, setFilter,
        filters: { problems: problem, cities: city, districts: district, workingWithPairs, workingOffline },
        citiesOptions, districtsOptions, workingWithPairsOptions, workingOfflineOptions } = Model.orderForm.filter;

    const { values: { comment }, setFieldValue } = useFormikContext();

    const setProblem = useCallback((problem) => {
        setFilter('problems', problem);
    }, []);
    const setCity = useCallback((city) => {
        setFilter('cities', city);
    }, []);
    const setDistrict = useCallback((district) => {
        setFilter('districts', district);
    }, []);
    const setWorkingWithPairs = useCallback((workingWithPairs) => {
        setFilter('workingWithPairs', workingWithPairs)
    }, []);
    const setWorkingOffline = useCallback((workingOffline) => {
        setFieldValue('workingOffline', workingOffline); //for validation purposes
        setFilter('workingOffline', workingOffline)
    }, []);
    const setComment = useCallback((value) => {
        setFieldValue('comment', value);
    }, []);

    const problemsProps = {
        problemsOptions,
        problem,
        setProblem,
        comment,
        setComment
    }

    const cityProps = {
        citiesOptions,
        setCity,
        city,
        workingOffline
    }

    const districtProps = {
        districtsOptions,
        setDistrict,
        district,
        city,
        workingOffline
    }

    const workingWithPairsProps = {
        workingWithPairs,
        setWorkingWithPairs,
        workingWithPairsOptions
    }

    const workingOfflineProps = {
        workingOffline,
        setWorkingOffline,
        workingOfflineOptions
    }

    return (
        <div className={ el('first-step') }>
            <div className={ el('number-block') }>
                <div className={ el('number') }><Text serif>1</Text></div>
            </div>
            <div className={ el('content-block') }>
                <div className={ el('fields', { disabled: !!specialist }) }>
                    <div className={ el('first-step-first-row') }>
                        <ProblemsAutocompleteField { ...problemsProps } />
                        {/*<OfflineCheckboxField/>*/ }
                    </div>
                    <div className={el('first-step-second-row')}>
                        <PairsCheckboxField { ...workingWithPairsProps } />
                        <OfflineRadioField {...workingOfflineProps } />
                        <span className={el('first-step-offline-fields', { hidden: !workingOffline })}>
                            <CityField { ...cityProps } />
                            <DistrictField { ...districtProps } />
                        </span>
                    </div>
                    <div className={ el('first-step-third-row') }>
                        <div className={ el('hint') }>
                            <Icon name={ 'info' }/>
                            <Text>Если у специалиста доступен только один тип встречи, выбор типа встречи будет не активен</Text>
                        </div>
                        <div className={ el('hint') }>
                            <Icon name={ 'info' }/>
                            <Text>В зависимости от выбранных параметров будут подобраны специалисты</Text>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

const SecondStep = observer(() => {

    const { choosePriority, setChoosePriority } = Model.orderForm;
    const onRadioClick = (e) => {
        const value = e.target.value;
        setChoosePriority(value);
    };

    return (
        <div className={ el('second-step') }>
            <div className={ el('number-block') }>
                <div className={ el('number') }><Text serif>2</Text></div>
            </div>
            <div className={ el('content-block') }>
                <RadioField name={ 'radio-field' } text={ 'К конкретному специалисту' }
                            checked={ choosePriority === 'specialist' }
                            comment={ 'Возможна запись на 1, 2, 5, 10 консультаций' } value={ 'specialist' }
                            onChange={ onRadioClick }/>
                <RadioField name={ 'radio-field' } text={ 'По дате/времени' } checked={ choosePriority === 'date' }
                            comment={ 'Только для разовых встреч' } value={ 'date' } onChange={ onRadioClick }/>
                <span className={el('events-field-container', { hidden: choosePriority === 'specialist' })} >
                    <EventsField />
                </span>
            </div>
        </div>
    );
});


const ThirdStep = observer(({
    teaserSize,
    showFinalStep,
    goToFinalStep,
}) => {

    const { isMobile } = useMobile();
    const { specialist } = Model.orderForm;

    return (
        <div className={ el('third-step') }>
            <div className={ el('number-block') }>
                <div className={ el('number') }><Text serif>3</Text></div>
            </div>
            <div className={ el('content-block') }>

                { !specialist &&
                    <Specialists teaserSize={ isMobile ? '' : teaserSize } goToFinalStep={ goToFinalStep }/> }
                { specialist && !showFinalStep &&
                    <TicketsBlock goToFinalStep={ goToFinalStep }/> }
            </div>
        </div>
    );
});


const Specialists = observer(({ teaserSize, goToFinalStep }) => {

    const { isMobile } = useMobile();
    const { specialistsData } = Model.specialist;
    const { loading } = Model.events;
    const { setName, name } = Model.orderForm.filter;

    const SpecialistsSlider = isMobile ? MobileSlider : Slider;
    const Fallback = <SpecialistsSlider items={ times((key) => <TeaserSkeleton key={ key } mod={ teaserSize }/>, 3) }/>
    return (
        <div className={ el('specialists-block') }>
            <div className={ el('specialists-header') }>
                <Text serif>Выберите специалиста</Text>
                <SpecialistSearchField names={ Model.orderForm.filtered.map(spec => spec.data.name) } name={name} setName={setName}/>
            </div>
            <div className={ el('specialists-teasers', teaserSize) }>
                <Suspense fallback={ Fallback }>
                    { loading && Fallback }
                    { !loading &&
                        <SliderBlock resource={ specialistsData } isMobile={ isMobile } teaserSize={ teaserSize }
                                     goToFinalStep={ goToFinalStep }/> }
                </Suspense>
            </div>
        </div>
    );
});

const SliderBlock = observer(({ resource, isMobile, teaserSize, goToFinalStep }) => {

    useEffect(() => {
        try {
            resource.read();
        } catch (err) {
            console.log(`error reading resource: ${JSON.stringify(err)}`);
        }
    }, []);

    const { filtered: specialists, choosePriority, setSpecialist, selectedEvents, workingOffline } = Model.orderForm;
    const { setFieldValue } = useFormikContext();
    const onSpecialistClick = (specialist) => () => {
        if (choosePriority === 'date' && selectedEvents[0]) {
            proceedToCheckout(() => {
                setSpecialist(specialist.data.name);
                setFieldValue('events', selectedEvents.map(e => specialist.getPK(e, workingOffline ? 'OFF' : 'ON')));
                goToFinalStep();
            })
        } else {
            setSpecialist(specialist.data.name);
        }
    }
    const SpecialistsSlider = isMobile ? MobileSlider : Slider;
    const teasers = useMemo(() =>
        specialists.map(spec =>
            <FormSpecialistTeaser
                specialist={ spec }
                key={ spec.data.id }
                mod={ teaserSize }
                onClick={ onSpecialistClick(spec) }
            />
        ), [ specialists, teaserSize ]
    );
    return <SpecialistsSlider items={ teasers }/>
});

const TicketsBlock = observer(({ goToFinalStep }) => {
    const {
        setSpecialist,
        selectedEvents,
        specialist,
        setSelectedEvents,
        amount,
        setAmount,
        setTicketType,
        ticketOptions,
        specialistEvents: events
    } = Model.orderForm;

    const { workingOffline } = Model.orderForm.filter.filters;
    const { setFieldValue } = useFormikContext();
    const onSaveClick = () => {
        proceedToCheckout(() => {
            setFieldValue('events', selectedEvents.map(e => specialist.getPK(e, workingOffline ? 'OFF' : 'ON')));
            goToFinalStep();
        })
    }
    const onSpecialistClick = () => {
        setSpecialist(null);
    }

    const ticketsProps = {
        specialist,
        selectedEvents,
        setSelectedEvents,
        amount,
        setAmount,
        setTicketType,
        onSaveClick,
        onSpecialistClick,
        ticketOptions,
        events
    }

    return <Tickets { ...ticketsProps } />
});

const FinalStep = observer(({ setShowFinalStep }) => {
    const { workingOffline, workingWithPairs, choosePriority, setSpecialist, specialist } = Model.orderForm;
    const { values: { events, comment } } = useFormikContext();

    const checkoutScreenProps = {
        workingOffline,
        specialist,
        events,
        comment,
        workingWithPairs
    }

    const onEditClick = useCallback(() => {
        if (choosePriority === 'date') {
            setShowFinalStep(false);
            setSpecialist(null);
        } else {
            setShowFinalStep(false);
        }
    }, []);

    return <OrderInfo onEditClick={ onEditClick } { ...checkoutScreenProps } />
});

const UserFields = observer(() => {
    const { workingOffline } = Model.orderForm.filter.filters;
    const { setFieldValue, values: { customerPhone } } = useFormikContext();
    const { isAuthenticated, username } = Model.user;

    const setPhone = useCallback((phone) => {
        setFieldValue('customerPhone', phone)
    }, []);

    const setCustomerFound = useCallback((isCustomerFound) => {
        setFieldValue('customerFound', isCustomerFound)
    }, []);

    const userInfoProps = {
        workingOffline,
        customerPhone,
        isAuthenticated,
        username,
        setPhone,
        setCustomerFound,
        setUsername: Model.orderForm.setUsername
    }

    return <UserInfo { ...userInfoProps } />
});

const SummaryBlock = observer(() => {
    const { price, submitting } = Model.orderForm;
    const { kzNotEnoughBalance } = Model.kz;

    return <Summary price={ price } submitting={ submitting } kzNotEnoughBalance={ kzNotEnoughBalance(price) }/>
});

const Form = ({ handleSubmit }) => {
    Model.specialist.stale = true;

    const [ teaserSize, setTeaserSize ] = useState('');

    useEffect(() => {
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.contentRect) {
                    const { height } = entry.contentRect;
                    if (height <= 700) {
                        setTeaserSize('x-small');
                        return;
                    }
                    if (height <= 800) {
                        setTeaserSize('small');
                        return;
                    }
                    if (height <= 900) {
                        setTeaserSize('medium');
                        return;
                    }
                    setTeaserSize('normal');
                }
            }
        });
        resizeObserver.observe(document.body);
        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const { specialist, price } = Model.orderForm;

    const [ showFinalStep, setShowFinalStep ] = useState(false);

    const goToFinalStep = () => {
        setShowFinalStep(true);
    };

    return (
        <form onSubmit={ handleSubmit }>
            { (!showFinalStep || !specialist) &&
                <>
                    <FirstStep/>
                    <SecondStep/>
                    <ThirdStep teaserSize={ teaserSize } showFinalStep={ showFinalStep }
                               goToFinalStep={ goToFinalStep }/>
                </>
            }
            { showFinalStep && specialist &&
                <>
                    <FinalStep setShowFinalStep={ setShowFinalStep }/>
                    <UserFields/>
                    <PaymentBalanceField mod={ 'long' } name={ 'saleSum' } price={ price }/>
                    <KZNotEnoughBalance price={ price }/>
                    { window.isRuDomain && <ForeignCardWidget /> }
                    <SummaryBlock />
                </>
            }
        </form>
    );
};

export default observer(Form);
